import { ThreeBounce } from "better-react-spinkit";
import { useContext, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FaCheckCircle } from "react-icons/fa";
import { IoWarningOutline } from "react-icons/io5";
import DocCategoryService from "../../pages/DocCategory/DocCategoryService";
import { DialogContext } from "../../store/context/DialogContext";
import { useTranslation } from "../../store/context/TranslationContext";
import { doDELETE, doGET, doPOST } from "../../util/HttpUtil";
import PrimaryButton from "../Buttons/PrimaryButton";
import DateSelectCustom from "../DateSelects/AntDateSelect";
import { translateLabel } from "../../util/Util";
import DocTypeSelectV2 from "./DocTypeSelectV2";
import DropzoneV2Compact from "./DropzoneV2Compact";
import { splitAttachments } from "../DocUpload/attachmentUtils";

const POLLING_INTERVAL = 15000;
const POLLING_TIMEOUT = 90000;

const removeEmptyPTags = (htmlString = "") =>
  htmlString.replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "");

const CardSkeleton = () => (
  <div className="docv2__card">
    <div className="docv2__skeleton">
      <div className="docv2__skeleton-row">
        <div style={{ flex: 1 }}>
          <Skeleton height={12} width="60%" />
          <Skeleton height={10} width="40%" />
        </div>
      </div>
      <Skeleton height={42} />
    </div>
  </div>
);

const DocFieldV2Compact = ({
  label,
  required = true,
  hint,
  showCategory,
  hideUpload,
  data = { attachment: { name: "", info: "", refNo: "", urls: [] }, version: 0 },
  record_id = "",
  getRecordId = async () => {},
  module = "",
  inline,
  field,
  asLabel,
  showExpiry,
  type,
  ubo_id,
  onSubmit = () => {},
  onDelete = () => {},
  seq,
  enableOnline,
  supportedFiles = ["image", "pdf"],
  single = false,
  error = "",
  validateByAi = false,
  onValidationStatusChange = () => {},
}) => {
  const { showError, showMessage, showConfirm } = useContext(DialogContext);
  const { t } = useTranslation();
  const [document, setDocument] = useState(data);
  const [loading, setLoading] = useState(false);
  const [validationStatus, setValidationStatus] = useState(document?.validationStatus || "NotStarted");
  const [isFetchingStatusOfAiValidation, setIsFetchingStatusOfAiValidation] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const pollingTimerRef = useRef(null);
  const startPollingTimeRef = useRef(null);
  const documentRef = useRef(null);

  useEffect(() => {
    try {
      DocCategoryService.fetchDocumentTypes();
    } catch (e) {}
  }, []);

  // Parcha proof-of-address validation now runs asynchronously server-side (~15-17 min) and is
  // created by the backend on document submit, so the applicant no longer initiates or polls it.
  // The previous auto-init (checkValidationStatus on mount / attachment change) is intentionally
  // removed; only the polling cleanup on unmount is retained as a safeguard.
  useEffect(() => {
    return () => clearPolling();
  }, []);

  useEffect(() => {
    if (validateByAi) onValidationStatusChange(document?._id, validationStatus);
  }, [validationStatus, document?._id]);

  useEffect(() => {
    let countdownTimer = null;
    if (validationStatus === "In Progress" && startPollingTimeRef.current) {
      countdownTimer = setInterval(() => {
        const elapsed = Date.now() - startPollingTimeRef.current;
        const timeLeft = POLLING_TIMEOUT - elapsed;
        if (timeLeft <= 0) {
          setRemainingTime(0);
          clearInterval(countdownTimer);
        } else {
          setRemainingTime(Math.ceil(timeLeft / 1000));
        }
      }, 1000);
    }
    return () => {
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [validationStatus]);

  useEffect(() => {
    setDocument(data);
    documentRef.current = data;
  }, [JSON.stringify(data)]);

  const clearPolling = () => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
      startPollingTimeRef.current = null;
    }
  };

  const startPolling = () => {
    startPollingTimeRef.current = Date.now();
    pollingTimerRef.current = setInterval(async () => {
      await checkValidationStatus();
    }, POLLING_INTERVAL);
  };

  const checkValidationStatus = async (docId) => {
    const documentId = docId || documentRef.current?._id;
    if (!record_id || !documentId || isFetchingStatusOfAiValidation) return;
    try {
      setIsFetchingStatusOfAiValidation(true);
      const response = await doGET(
        `/api/application-doc/init-status?applicationId=${record_id}&documentId=${documentId}`
      );
      const status = response?.data;
      setValidationStatus(status);
      if (status === "Valid" || status === "Invalid") {
        clearPolling();
      } else {
        if (startPollingTimeRef.current) {
          const elapsed = Date.now() - startPollingTimeRef.current;
          if (elapsed >= POLLING_TIMEOUT) {
            setValidationStatus("Valid");
            clearPolling();
          }
        } else {
          startPolling();
        }
      }
      setIsFetchingStatusOfAiValidation(false);
    } catch (err) {
      showError(err);
      clearPolling();
      setIsFetchingStatusOfAiValidation(false);
    }
  };

  const getLatestVersionOfDoc = async (validateByAi, updatedField) => {
    if (validateByAi && updatedField === "docCategory_id") {
      try {
        const gridResp = await doGET(
          `/api/application-doc/grid?rows=-1&form_id=${record_id}${ubo_id ? `&ubo_id=${ubo_id}` : ""}${
            seq ? `&seq=${seq}` : ""
          }${type ? `&type=${type}` : ""}`
        );
        if (gridResp.status === 200) return gridResp.data?.rows?.[0];
      } catch (err) {
        showError(err);
        return {};
      }
    }
    return {};
  };

  const handleSaveCard = async (doc, updatedField) => {
    const supportedExt = supportedFiles.reduce((acc, fileType) => {
      if (fileType === "image") acc.push(".png", ".jpeg", ".jpg");
      else if (fileType === "pdf") acc.push(".pdf");
      else if (fileType === "doc") acc.push(".doc", ".docx", ".txt");
      else if (fileType === "ppt") acc.push(".ppt", ".pptx");
      return acc;
    }, []);

    const invalidFiles = (doc?.attachment?.urls ?? []).filter((url) => {
      if (url !== undefined && (url?.name || url?.key)) {
        const ext = (url?.name ?? url?.key).slice((url?.name ?? url?.key).lastIndexOf("."));
        return !supportedExt.includes(ext.toLowerCase());
      }
      return true;
    });

    if (invalidFiles.length > 0) {
      showError(`Unsupported file format detected. Please upload only ${supportedFiles.join(" or ")} files.`);
      return;
    }

    setLoading(true);

    const formData = new FormData();
    const { stored: formUrls, local: newFiles } = splitAttachments(doc?.attachment?.urls);
    newFiles.forEach((file) => formData.append("file", file));

    const recordId = record_id || (await getRecordId());
    const latestDocument = await getLatestVersionOfDoc(validateByAi, updatedField);

    const docObject =
      validateByAi && updatedField === "docCategory_id"
        ? {
            form_id: recordId,
            record_id,
            ubo_id,
            type,
            ...latestDocument,
            docCategory_id: doc?.docCategory_id,
          }
        : {
            record_id,
            module,
            expiry: doc?.expiry,
            version: latestDocument?.version ?? doc.version,
            docCategory_id: doc?.docCategory_id,
            ubo_id,
            form_id: recordId,
            type,
            ...(seq && { seq }),
            attachment: { ...doc.attachment, urls: formUrls },
            ...(data._id && { _id: data._id }),
            ...(field && { field }),
          };

    formData.append("data", JSON.stringify(docObject));

    try {
      const response = await doPOST(`/api/${module ? "file" : "application-doc"}/upload`, formData);
      if (response.status === 200) {
        showMessage(
          updatedField === "docCategory_id" ? "Updated Document Category" : "Document Uploaded"
        );
        setDocument(response.data);
        documentRef.current = response.data;
        onSubmit();
        // Parcha POA validation is now created server-side on submit (~15-17 min, async); the
        // applicant no longer initiates it here. Non-AI documents still resolve immediately.
        if (!validateByAi) setValidationStatus("Valid");
        return null;
      } else if (response?.status === 413) {
        showError(response?.data);
        return null;
      }
    } catch (err) {
      showError(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleAttachmentDelete = async (index) => {
    if (
      await showConfirm({
        title: t("Do you want to delete file?"),
        description: t("This is an unrecoverable operation."),
      })
    ) {
      setLoading(true);
      try {
        const response = await doDELETE(`/api/${module ? "file" : "application-doc"}/delete/attachment`, {
          record_id,
          _id: document._id,
          module,
          index,
        });
        if (response.status === 200) {
          setDocument(response.data);
          documentRef.current = response.data;
          showMessage("Deleted");
          if (!response.data.attachment?.urls?.length) {
            clearPolling();
            setValidationStatus("NotStarted");
          }
          onDelete();
          return true;
        }
      } catch (e) {
        showError(e);
      } finally {
        setLoading(false);
      }
    }
    return false;
  };

  const handleFieldChange = async (fieldKey, value) => {
    const updated = { ...document, [fieldKey]: value };
    await handleSaveCard(updated, fieldKey);
  };

  const hasFile = !!document?.attachment?.urls?.length;

  const showError_ = !!error?.length && !hasFile && !loading;
  const isInvalid = hasFile && validateByAi && validationStatus === "Invalid";

  const cardClass = [
    "docv2__card",
    hasFile && !isInvalid ? "docv2__card--has-file" : "",
    showError_ ? "docv2__card--error" : "",
    isInvalid ? "docv2__card--invalid" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderValidationAlert = () => {
    if (!validateByAi || !document?._id || !hasFile) return null;
    if (validationStatus === "Valid") {
      return (
        <div className="docv2__alert docv2__alert--success">
          <FaCheckCircle className="docv2__alert-icon" />
          <strong>{t("Document Verified")}</strong>
        </div>
      );
    }
    if (validationStatus === "Invalid") {
      return (
        <div className="docv2__alert docv2__alert--danger">
          <IoWarningOutline className="docv2__alert-icon" />
          <div>
            <strong>{t("Invalid Document")}</strong>
            {DocCategoryService?.documentTypes?.[showCategory]?.warningMessage ? (
              <p
                className="no-default-p"
                dangerouslySetInnerHTML={{
                  __html: translateLabel(
                    removeEmptyPTags(DocCategoryService?.documentTypes?.[showCategory]?.warningMessage ?? ""),
                    t
                  ),
                }}
              />
            ) : (
              <>
                <p>
                  {t(
                    "The document you provided may be invalid, We recommend uploading another document."
                  )}
                </p>
                <ul>
                  <li>{t("Ensure the document is still valid (not expired).")}</li>
                  <li>{t("Confirm that all information matches the details in your application.")}</li>
                  <li>{t("Verify that the full document is clearly visible, including its borders.")}</li>
                </ul>
              </>
            )}
          </div>
        </div>
      );
    }
    if (validationStatus === "In Progress") {
      return (
        <div className="docv2__alert docv2__alert--warning">
          <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <ThreeBounce color="orange" size={7} />
              <span>{t("Verifying document, please wait...")}</span>
            </div>
            {remainingTime > 0 && (
              <div style={{ fontSize: 10.5 }}>
                {t("Estimated time remaining:")} {remainingTime} {t("seconds")}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) return <CardSkeleton />;

  return (
    <div className={cardClass}>
      <div className="docv2__header">
        <div className="docv2__title-wrap">
          <div className="docv2__title">
            <span className="docv2__title-text">
              {label}
              {required ? <span className="docv2__required">*</span> : null}
            </span>
          </div>
          {hint ? <div className="docv2__hint">{hint}</div> : null}
        </div>
      </div>

      <div className="docv2__body">
        {showError_ ? <em className="docv2__error">{error}</em> : null}

        <DropzoneV2Compact
          asLabel={asLabel}
          value={document?.attachment?.urls}
          onDeleteAttachment={handleAttachmentDelete}
          supportedFiles={supportedFiles}
          single={single}
          onChange={(v) =>
            handleFieldChange("attachment", { ...(document?.attachment ?? {}), urls: v })
          }
        />

        {(showCategory || showExpiry || (!asLabel && !hideUpload)) && hasFile ? (
          <div
            className={`docv2__meta-row ${!asLabel && !hideUpload ? "docv2__meta-row--with-upload" : ""}`}
          >
            {showCategory && (
              <div className="docv2__meta-cell">
                <DocTypeSelectV2
                  module={showCategory}
                  label={t("Document Type")}
                  helperText="Select type"
                  value={document?.docCategory_id}
                  onChange={(option) => handleFieldChange("docCategory_id", option?.value || "")}
                />
              </div>
            )}

            {showExpiry && (
              <div className="docv2__meta-cell">
                <label className="docv2__select-label">{t("Expiry Date")}</label>
                <DateSelectCustom
                  value={document?.expiry}
                  placeholder={"dd mmm yyyy"}
                  displayFormat="dd MMM yyyy"
                  format="dd-mmm-yyyy"
                  onChange={(e) => handleFieldChange("expiry", e)}
                  isDisabled={asLabel}
                  required
                  type="text"
                  minDate={!asLabel ? new Date() : undefined}
                />
              </div>
            )}

            {!asLabel && !hideUpload && (
              <div className="docv2__upload-btn-cell">
                <PrimaryButton
                  style={{ fontSize: 12, minHeight: 34, borderRadius: 8 }}
                  loading={loading}
                  isDisabled={loading || !hasFile || (showExpiry && !document?.expiry)}
                  onClick={() => handleSaveCard(document)}
                >
                  {loading ? <ThreeBounce color="white" size={7} /> : t("Upload")}
                </PrimaryButton>
              </div>
            )}
          </div>
        ) : null}

        {renderValidationAlert()}
      </div>
    </div>
  );
};

export default DocFieldV2Compact;
